<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

/**
 * Supported providers (must match server's SUPPORTED_PROVIDERS constant).
 * Rendered as a select list when creating a new token.
 */
const SUPPORTED_PROVIDERS = ['openai', 'gemini', 'claude', 'anthropic'];

/**
 * Supported services (must match server's SUPPORTED_SERVICES constant).
 * Used to differentiate between different service types.
 */
const SUPPORTED_SERVICES = ['RESPONDING', 'AGENT_BUILDER'];

const tokens = computed(() => getters['llmProviderTokens/getTokens'].value);
const uiFlags = computed(() => getters['llmProviderTokens/getUIFlags'].value);

const loadingRow = ref({});
const justCreatedMask = ref(null);
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);

const selectedToken = ref(null);

/**
 * Forms
 */
const addForm = ref({
  provider_name: '',
  access_token: '',
  active: true,
  service: 'RESPONDING',
});

const editForm = ref({
  id: null,
  provider_name: '',
  active: true,
  rotate_access_token: '', // optional new token to rotate
  service: 'RESPONDING',
});

const addErrors = ref({
  provider_name: '',
  access_token: '',
  service: '',
});

const editErrors = ref({
  rotate_access_token: '',
});

/**
 * Helpers
 */
const resetAddForm = () => {
  addForm.value = {
    provider_name: '',
    access_token: '',
    active: true,
    service: 'RESPONDING',
  };
  addErrors.value = { provider_name: '', access_token: '', service: '' };
};

const resetEditForm = token => {
  editForm.value = {
    id: token.id,
    provider_name: token.provider_name,
    active: token.active,
    rotate_access_token: '',
    service: token.service || 'RESPONDING',
  };
  editErrors.value = { rotate_access_token: '' };
};

const validateAddForm = () => {
  let valid = true;
  addErrors.value = { provider_name: '', access_token: '', service: '' };

  if (!addForm.value.provider_name) {
    addErrors.value.provider_name = t(
      'LLM_PROVIDER_TOKENS.FORM.PROVIDER.REQUIRED'
    );
    valid = false;
  } else if (!SUPPORTED_PROVIDERS.includes(addForm.value.provider_name)) {
    addErrors.value.provider_name = t(
      'LLM_PROVIDER_TOKENS.FORM.PROVIDER.INVALID'
    );
    valid = false;
  }

  if (!addForm.value.access_token || !addForm.value.access_token.trim()) {
    addErrors.value.access_token = t(
      'LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.REQUIRED'
    );
    valid = false;
  }

  if (!addForm.value.service) {
    addErrors.value.service = t('LLM_PROVIDER_TOKENS.FORM.SERVICE.REQUIRED');
    valid = false;
  } else if (!SUPPORTED_SERVICES.includes(addForm.value.service)) {
    addErrors.value.service = t('LLM_PROVIDER_TOKENS.FORM.SERVICE.INVALID');
    valid = false;
  }

  return valid;
};

const validateEditForm = () => {
  let valid = true;
  editErrors.value = { rotate_access_token: '' };
  // Rotation token optional; if provided ensure some length
  if (
    editForm.value.rotate_access_token &&
    editForm.value.rotate_access_token.trim().length < 8
  ) {
    editErrors.value.rotate_access_token = t(
      'LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.MIN_LENGTH',
      { count: 8 }
    );
    valid = false;
  }
  return valid;
};

/**
 * Modal open/close
 */
const openAddModal = () => {
  resetAddForm();
  showAddModal.value = true;
};
const closeAddModal = () => {
  showAddModal.value = false;
};

const openEditModal = token => {
  selectedToken.value = token;
  resetEditForm(token);
  showEditModal.value = true;
};
const closeEditModal = () => {
  showEditModal.value = false;
};

const openDeleteModal = token => {
  selectedToken.value = token;
  showDeleteModal.value = true;
};
const closeDeleteModal = () => {
  showDeleteModal.value = false;
};

/**
 * CRUD actions
 */
const createToken = async () => {
  if (!validateAddForm()) return;
  try {
    const plain = (addForm.value.access_token || '').trim();
    const prov = addForm.value.provider_name;
    await store.dispatch('llmProviderTokens/create', {
      provider_name: prov,
      access_token: plain,
      active: addForm.value.active,
      service: addForm.value.service,
    });
    // show masked token info once
    justCreatedMask.value = { provider_name: prov, last4: plain.slice(-4) };
    // clear sensitive data from memory
    resetAddForm();
    useAlert(t('LLM_PROVIDER_TOKENS.CREATE.SUCCESS'));
    closeAddModal();
  } catch (e) {
    useAlert(e?.message || t('LLM_PROVIDER_TOKENS.CREATE.ERROR'));
  }
};

const updateToken = async () => {
  if (!validateEditForm()) return;
  try {
    const payload = {
      id: editForm.value.id,
      active: editForm.value.active,
    };
    if (editForm.value.rotate_access_token) {
      payload.access_token = editForm.value.rotate_access_token.trim();
    }
    await store.dispatch('llmProviderTokens/update', payload);
    useAlert(t('LLM_PROVIDER_TOKENS.UPDATE.SUCCESS'));
    closeEditModal();
  } catch (e) {
    useAlert(e?.message || t('LLM_PROVIDER_TOKENS.UPDATE.ERROR'));
  }
};

const deleteToken = async () => {
  if (!selectedToken.value) return;
  loadingRow.value[selectedToken.value.id] = true;
  closeDeleteModal();
  try {
    await store.dispatch('llmProviderTokens/delete', selectedToken.value.id);
    useAlert(t('LLM_PROVIDER_TOKENS.DELETE.SUCCESS'));
  } catch (e) {
    useAlert(e?.message || t('LLM_PROVIDER_TOKENS.DELETE.ERROR'));
  } finally {
    loadingRow.value[selectedToken.value.id] = false;
  }
};

// Quick toggle active/inactive without opening modal
const toggleActive = async token => {
  loadingRow.value[token.id] = true;
  try {
    if (token.active) {
      await store.dispatch('llmProviderTokens/deactivate', token.id);
    } else {
      await store.dispatch('llmProviderTokens/activate', token.id);
    }
  } catch (e) {
    useAlert(e?.message || t('LLM_PROVIDER_TOKENS.UPDATE.ERROR'));
  } finally {
    loadingRow.value[token.id] = false;
  }
};

/**
 * Table headers
 */
const tableHeaders = computed(() => [
  t('LLM_PROVIDER_TOKENS.TABLE_HEADER.PROVIDER'),
  t('LLM_PROVIDER_TOKENS.TABLE_HEADER.SERVICE'),
  t('LLM_PROVIDER_TOKENS.TABLE_HEADER.STATUS'),
  t('LLM_PROVIDER_TOKENS.TABLE_HEADER.TOKEN'),
  t('LLM_PROVIDER_TOKENS.TABLE_HEADER.UPDATED'),
]);

/**
 * Derived display helpers
 */
const formatStatus = token =>
  token.active
    ? t('LLM_PROVIDER_TOKENS.STATUS.ACTIVE')
    : t('LLM_PROVIDER_TOKENS.STATUS.INACTIVE');

const formatTokenPresence = token =>
  token.has_token
    ? t('LLM_PROVIDER_TOKENS.TOKEN.SET')
    : t('LLM_PROVIDER_TOKENS.TOKEN.MISSING');

const providerLabel = provider =>
  t(`LLM_PROVIDER_TOKENS.PROVIDERS.${provider}`, provider);

const serviceLabel = service =>
  t(`LLM_PROVIDER_TOKENS.SERVICES.${service}`, service);

/**
 * Lifecycle
 */
onBeforeMount(() => {
  store.dispatch('llmProviderTokens/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LLM_PROVIDER_TOKENS.LOADING')"
    :no-records-found="!tokens.length"
    :no-records-message="$t('LLM_PROVIDER_TOKENS.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('LLM_PROVIDER_TOKENS.HEADER')"
        :description="$t('LLM_PROVIDER_TOKENS.DESCRIPTION')"
        :link-text="$t('LLM_PROVIDER_TOKENS.LEARN_MORE')"
        feature-name="llm_provider_tokens"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('LLM_PROVIDER_TOKENS.ADD.BUTTON')"
            :is-loading="uiFlags.isCreating"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <div
        v-if="justCreatedMask"
        class="mb-4 flex items-center justify-between rounded border border-primary-200 bg-primary-25 p-3 text-sm text-primary-700"
      >
        <div>
          {{ providerLabel(justCreatedMask.provider_name) }} — ****{{
            justCreatedMask.last4
          }}.
          {{ $t('LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.HELP') }}
        </div>
        <Button
          xs
          slate
          faded
          :label="$t('DIALOG.BUTTONS.CONFIRM')"
          @click="justCreatedMask = null"
        />
      </div>
    </template>
    <template #body>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak">
          <thead>
            <tr>
              <th
                v-for="header in tableHeaders"
                :key="header"
                class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
              >
                {{ header }}
              </th>
              <th class="py-4 font-semibold text-right text-n-slate-11">
                {{ $t('TABLE.HEADER.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-12">
            <tr v-for="token in tokens" :key="token.id">
              <td class="py-4 ltr:pr-4 rtl:pl-4 font-medium">
                {{ providerLabel(token.provider_name) }}
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700"
                  >{{ serviceLabel(token.service) }}</span
                >
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                  :class="
                    token.active
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-700'
                  "
                  >{{ formatStatus(token) }}</span
                >
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                  :class="
                    token.has_token
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-amber-100 text-amber-700'
                  "
                  >{{ formatTokenPresence(token) }}</span
                >
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4 text-xs">
                {{ new Date(token.updated_at).toLocaleString() }}
              </td>
              <td class="py-4 min-w-xs">
                <div class="flex gap-1 justify-end">
                  <Button
                    v-tooltip.top="$t('COMPONENTS.SWITCH.TOGGLE')"
                    :icon="
                      token.active
                        ? 'i-lucide-toggle-right'
                        : 'i-lucide-toggle-left'
                    "
                    xs
                    faded
                    :is-loading="loadingRow[token.id]"
                    @click="toggleActive(token)"
                  />
                  <Button
                    v-tooltip.top="$t('LLM_PROVIDER_TOKENS.ACTIONS.EDIT')"
                    icon="i-lucide-pen"
                    slate
                    xs
                    faded
                    :is-loading="loadingRow[token.id]"
                    @click="openEditModal(token)"
                  />
                  <Button
                    v-tooltip.top="$t('LLM_PROVIDER_TOKENS.ACTIONS.DELETE')"
                    icon="i-lucide-trash-2"
                    ruby
                    xs
                    faded
                    :is-loading="loadingRow[token.id]"
                    @click="openDeleteModal(token)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Add Modal -->
    <woot-modal v-model:show="showAddModal" :on-close="closeAddModal">
      <div class="p-4">
        <h3 class="text-base font-semibold mb-4">
          {{ $t('LLM_PROVIDER_TOKENS.FORM.ADD.TITLE') }}
        </h3>
        <form class="space-y-4" @submit.prevent="createToken">
          <!-- Provider -->
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.PROVIDER.LABEL') }}
            </label>
            <select
              v-model="addForm.provider_name"
              :disabled="uiFlags.isCreating"
            >
              <option value="">
                {{ $t('LLM_PROVIDER_TOKENS.FORM.PROVIDER.PLACEHOLDER') }}
              </option>
              <option
                v-for="provider in SUPPORTED_PROVIDERS"
                :key="provider"
                :value="provider"
              >
                {{ providerLabel(provider) }}
              </option>
            </select>
            <p
              v-if="addErrors.provider_name"
              class="mt-1 text-xs text-ruby-600"
            >
              {{ addErrors.provider_name }}
            </p>
          </div>

          <!-- Access Token -->
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.LABEL') }}
            </label>
            <textarea
              v-model="addForm.access_token"
              rows="3"
              :placeholder="
                $t('LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.PLACEHOLDER')
              "
              :disabled="uiFlags.isCreating"
            />
            <p v-if="addErrors.access_token" class="mt-1 text-xs text-ruby-600">
              {{ addErrors.access_token }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.HELP') }}
            </p>
          </div>

          <!-- Service -->
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.SERVICE.LABEL') }}
            </label>
            <select v-model="addForm.service" :disabled="uiFlags.isCreating">
              <option value="">
                {{ $t('LLM_PROVIDER_TOKENS.FORM.SERVICE.PLACEHOLDER') }}
              </option>
              <option
                v-for="service in SUPPORTED_SERVICES"
                :key="service"
                :value="service"
              >
                {{ serviceLabel(service) }}
              </option>
            </select>
            <p v-if="addErrors.service" class="mt-1 text-xs text-ruby-600">
              {{ addErrors.service }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.SERVICE.HELP') }}
            </p>
          </div>

          <!-- Active toggle -->
          <div class="flex items-center gap-2">
            <input
              id="add_token_active"
              v-model="addForm.active"
              type="checkbox"
              class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              :disabled="uiFlags.isCreating"
            />
            <label for="add_token_active" class="text-sm">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.ACTIVE.LABEL') }}
            </label>
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <Button
              slate
              :label="$t('LLM_PROVIDER_TOKENS.FORM.CANCEL')"
              :disabled="uiFlags.isCreating"
              @click="closeAddModal"
            />
            <Button
              primary
              type="submit"
              :label="$t('LLM_PROVIDER_TOKENS.FORM.SAVE')"
              :is-loading="uiFlags.isCreating"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- Edit Modal -->
    <woot-modal v-model:show="showEditModal" :on-close="closeEditModal">
      <div v-if="selectedToken" class="p-4">
        <h3 class="text-base font-semibold mb-4">
          {{ $t('LLM_PROVIDER_TOKENS.FORM.EDIT.TITLE') }}
        </h3>
        <form class="space-y-4" @submit.prevent="updateToken">
          <!-- Provider name (readonly) -->
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.PROVIDER.LABEL') }}
            </label>
            <input
              :value="providerLabel(editForm.provider_name)"
              type="text"
              readonly
              class="w-full rounded border border-n-weak bg-gray-50 px-3 py-2 text-sm text-n-slate-12"
            />
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.PROVIDER.READONLY_HINT') }}
            </p>
          </div>

          <!-- Service (readonly) -->
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.SERVICE.LABEL') }}
            </label>
            <input
              :value="serviceLabel(editForm.service)"
              type="text"
              readonly
              class="w-full rounded border border-n-weak bg-gray-50 px-3 py-2 text-sm text-n-slate-12"
            />
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.SERVICE.READONLY_HINT') }}
            </p>
          </div>

          <!-- Active toggle -->
          <div class="flex items-center gap-2">
            <input
              id="edit_token_active"
              v-model="editForm.active"
              type="checkbox"
              class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              :disabled="uiFlags.isUpdating"
            />
            <label for="edit_token_active" class="text-sm">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.ACTIVE.LABEL') }}
            </label>
          </div>

          <!-- Rotate token -->
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.ROTATE_LABEL') }}
            </label>
            <textarea
              v-model="editForm.rotate_access_token"
              rows="3"
              :placeholder="
                $t('LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.ROTATE_PLACEHOLDER')
              "
              :disabled="uiFlags.isUpdating"
            />
            <p
              v-if="editErrors.rotate_access_token"
              class="mt-1 text-xs text-ruby-600"
            >
              {{ editErrors.rotate_access_token }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('LLM_PROVIDER_TOKENS.FORM.ACCESS_TOKEN.ROTATE_HELP') }}
            </p>
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <Button
              slate
              :label="$t('LLM_PROVIDER_TOKENS.FORM.CANCEL')"
              :disabled="uiFlags.isUpdating"
              @click="closeEditModal"
            />
            <Button
              primary
              type="submit"
              :label="$t('LLM_PROVIDER_TOKENS.FORM.UPDATE')"
              :is-loading="uiFlags.isUpdating"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- Delete Modal -->
    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="deleteToken"
      :title="$t('LLM_PROVIDER_TOKENS.DELETE.CONFIRM.TITLE')"
      :message="$t('LLM_PROVIDER_TOKENS.DELETE.CONFIRM.MESSAGE')"
      :message-value="
        selectedToken ? providerLabel(selectedToken.provider_name) : ''
      "
      :confirm-text="$t('LLM_PROVIDER_TOKENS.DELETE.CONFIRM.YES')"
      :reject-text="$t('LLM_PROVIDER_TOKENS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
